import { useState } from "react";
import { IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import type { FormRow } from "../types";

function summarizeObject(value: unknown): string {
  if (!value || typeof value !== "object" || Array.isArray(value)) return "—";
  const keys = Object.keys(value as Record<string, unknown>);
  return `{ ${keys.length} ${keys.length === 1 ? "field" : "fields"} }`;
}

function summarizeArray(value: unknown): string {
  if (!Array.isArray(value)) return "[ 0 items ]";
  return `[ ${value.length} ${value.length === 1 ? "item" : "items"} ]`;
}

function isEmpty(value: unknown, fieldType: string): boolean {
  if (fieldType === "quantity") {
    if (!value || typeof value !== "object" || Array.isArray(value)) return true;
    const qv = (value as { value?: unknown }).value;
    return qv === undefined || qv === null || qv === "";
  }
  if (value === undefined || value === null || value === "") return true;
  if (fieldType === "object" || fieldType === "anyOfTabs" || fieldType === "oneOf") {
    if (typeof value === "object" && !Array.isArray(value)) {
      return Object.keys(value as Record<string, unknown>).length === 0;
    }
  }
  if (fieldType === "array") {
    if (Array.isArray(value)) return value.length === 0;
  }
  return false;
}

export const ValueCellRenderer = (props: { data?: FormRow; context?: any }) => {
  const row = props.data;
  const [revealed, setRevealed] = useState(false);
  if (!row) return null;

  if (row.fieldType === "oneOf") {
    const hasValue = row.value && typeof row.value === "object" && Object.keys(row.value as object).length > 0;
    // Optional-section toggle off → show explicit "(disabled)"; that's the only
    // place this label belongs.
    if (row.isOptionalSectionParent && !row.optionalSectionEnabled) {
      return <span style={{ color: "#5d6c8a", fontStyle: "italic" }}>(disabled)</span>;
    }
    if (hasValue) {
      const active = row.oneOfBranches?.[row.activeOneOfIndex ?? 0];
      return (
        <span style={{ color: "#9fa8da", fontStyle: "italic" }}>
          {active?.title ?? "—"}
        </span>
      );
    }
    // Unset oneOf → fall through to the empty / ghost-button rendering below.
  }

  const v = row.value;
  if (isEmpty(v, row.fieldType)) {
    const isContainer = row.fieldType === "object" || row.fieldType === "array" || row.fieldType === "anyOfTabs";
    const isOneOf = row.fieldType === "oneOf";
    const containerLabel = row.fieldType === "array" ? "Add item" : isOneOf ? "Pick type" : "Configure";
    const label = row.required
      ? (isContainer || isOneOf)
        ? containerLabel
        : "Required"
      : (isContainer || isOneOf)
        ? containerLabel
        : "Set";
    const hint =
      !isContainer && row.defaultValue !== undefined && row.defaultValue !== null
        ? ` default ${String(row.defaultValue)}`
        : "";
    const color = row.required ? "#ff1744" : "#7a8db3";
    const hintColor = row.required ? "rgba(255,23,68,0.7)" : "#5d6c8a";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          color,
          fontSize: 11,
          fontStyle: "italic",
          padding: "0.5px 6px",
          lineHeight: 1.5,
          borderRadius: 3,
          border: `1px dashed ${color}`,
          cursor: "pointer",
        }}
      >
        <span style={{ fontWeight: 700, fontStyle: "normal" }}>+</span>
        {label}
        {hint && <span style={{ color: hintColor }}>·{hint}</span>}
      </span>
    );
  }

  if (row.fieldType === "object" || row.fieldType === "anyOfTabs") {
    return <span style={{ color: "#888", fontStyle: "italic" }}>{summarizeObject(row.value)}</span>;
  }
  if (row.fieldType === "array") {
    return <span style={{ color: "#888", fontStyle: "italic" }}>{summarizeArray(row.value)}</span>;
  }

  if (row.isSecret) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontFamily: "monospace" }}>
          {revealed ? String(v) : "••••••••"}
        </span>
        <IconButton
          size="small"
          aria-label={revealed ? "Hide value" : "Show value"}
          onClick={(e) => {
            e.stopPropagation();
            setRevealed((r) => !r);
          }}
        >
          {revealed ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      </span>
    );
  }

  if (row.fieldType === "boolean") {
    return <span>{v ? "true" : "false"}</span>;
  }

  if (row.fieldType === "quantity") {
    const q = (v ?? {}) as { value?: unknown; unit?: unknown };
    const unitLabel = q.unit ? String(q.unit).toUpperCase() : "native";
    return (
      <span>
        {String(q.value)}{" "}
        <span style={{ color: "#7a8db3" }}>{unitLabel}</span>
      </span>
    );
  }

  return <span>{String(v)}</span>;
};
